import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { IDateParams } from 'ag-grid-enterprise';
import flatpickr from 'flatpickr';
import style from './DateTimeInput.module.scss';
import CsbErrorBoundary from 'components/CsbErrorBoudary/CsbErrorBoundary';
import { useAppSelector } from 'redux/hooks';
import { selectFilterModel } from 'redux/dashboard/DashboardSlice';

export default forwardRef((props: IDateParams, ref) => {
  const defaultDate = new Date(new Date().setHours(0, 0, 0, 0));
  const [date, setDate] = useState<Date | null>(defaultDate);
  const filterModelState = useAppSelector(selectFilterModel);
  const dateModified = useRef<boolean>(false);
  const timeModified = useRef<boolean>(false);
  const programmaticUpdate = useRef<boolean>(false);
  const isResetWithDefaults = useRef<boolean>(false);
  const [time, setTime] = useState<string>('');
  const [picker, setPicker] = useState<any>(null);
  const [timePicker, setTimePicker] = useState<any>(null);
  const refFlatPickr = useRef(null);
  const refFlatTimePickr = useRef(null);
  const refInput = useRef<HTMLInputElement>(null);
  const refTimeInput = useRef<HTMLInputElement>(null);

  // we use a ref as well as state, as state is async,
  // and after the grid calls setDate() (eg when setting filter model)
  // it then can call getDate() immediately (eg to execute the filter)
  // and we need to pass back the most recent value, not the old 'current state'.
  const dateRef = useRef<any>(null);

  //*********************************************************************************
  //          LINKING THE UI, THE STATE AND AG-GRID
  //*********************************************************************************

  const onDateChanged = (selectedDates: any) => {
    const newDate = selectedDates[0];

    // Handle case when newDate is undefined (e.g., after reset/clear)
    if (!newDate) {
      return;
    }

    // Preserve the time from timePicker if it exists
    if (dateRef.current) {
      newDate.setHours(
        dateRef.current.getHours(),
        dateRef.current.getMinutes()
      );
    }

    setDate(newDate);
    dateRef.current = newDate;
    props.onDateChanged();
    dateModified.current = true;

    // Exit reset state when user modifies date
    isResetWithDefaults.current = false;
  };

  const onTimeChanged = (selectedDates: any) => {
    const newTime = selectedDates[0];

    // Handle case when newTime is undefined (e.g., after reset/clear)
    if (!newTime) {
      return;
    }

    const currentDate = dateRef.current
      ? new Date(dateRef.current)
      : new Date();
    currentDate.setHours(newTime.getHours(), newTime.getMinutes());
    setTime(currentDate?.toString()?.slice(16, 21));
    dateRef.current = currentDate;
    props.onDateChanged();
    timeModified.current = true;

    // Exit reset state when user modifies time
    isResetWithDefaults.current = false;
  };

  useEffect(() => {
    setPicker(
      //@ts-ignore
      flatpickr(refFlatPickr.current, {
        onChange: onDateChanged,
        dateFormat: 'Y-m-d',
        wrap: true,
        defaultDate,
      })
    );

    setTimePicker(
      //@ts-ignore
      flatpickr(refFlatTimePickr.current, {
        onChange: onTimeChanged,
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
        defaultDate: defaultDate?.toString().slice(16, 21),
      })
    );

    onDateChanged([defaultDate]);
    onTimeChanged([defaultDate]);

    return () => {};
  }, []);

  useEffect(() => {
    if (picker) {
      picker.calendarContainer.classList.add('ag-custom-component-popup');
    }

    if (timePicker) {
      timePicker.calendarContainer.classList.add('ag-custom-component-popup');
    }
    return () => {};
  }, [picker, timePicker]);

  useEffect(() => {
    // Update flatpickr instances only for user modifications, not programmatic updates
    if (picker && dateModified.current && !programmaticUpdate.current && date) {
      picker.setDate(date);
    }

    if (timePicker && timeModified.current && !programmaticUpdate.current) {
      timePicker.setDate(time);
    }
    return () => {};
  }, [date, time, timePicker, picker]);

  useImperativeHandle(ref, () => ({
    //*********************************************************************************
    //          METHODS REQUIRED BY AG-GRID
    //*********************************************************************************
    getDate() {
      dateModified.current = false;
      timeModified.current = false;

      // If we're in reset state or dateRef is null, return null (no filter active)
      if (isResetWithDefaults.current || !dateRef.current) {
        return null;
      }

      return dateRef.current;
    },

    setDate(date: any) {
      dateModified.current = false;
      timeModified.current = false;
      programmaticUpdate.current = true;

      // Handle reset case when date is null or undefined
      if (date == null) {
        // Clear all values completely for true reset
        dateRef.current = null;
        setDate(null);
        setTime('');

        // Clear flatpickr instances
        if (picker) {
          picker.clear();
        }
        if (timePicker) {
          timePicker.clear();
        }

        // Mark as reset state
        isResetWithDefaults.current = true;
        dateModified.current = false;
        timeModified.current = false;
      } else {
        dateRef.current = date;
        setDate(date);
        setTime(date?.toString().slice(16, 21));

        // Update flatpickr instances for programmatic updates
        if (picker) {
          picker.setDate(date);
        }
        if (timePicker) {
          timePicker.setDate(date);
        }
      }

      programmaticUpdate.current = false;
    },

    //*********************************************************************************
    //          AG-GRID OPTIONAL METHODS
    //*********************************************************************************

    setInputPlaceholder(placeholder: string) {
      if (!isResetWithDefaults.current) {
        handleInputPlaceholder();
      }
    },
    setInputAriaLabel(label: string) {
      if (refInput.current) {
        refInput.current.setAttribute('aria-label', label);
      }
    },
  }));

  refInput.current?.addEventListener('click', () => {
    dateModified.current = true;
  });

  refTimeInput.current?.addEventListener('click', () => {
    timeModified.current = true;
  });

  const handleInputPlaceholder = () => {
    if (refInput.current) {
      const isFilterApplied =
        filterModelState && Object.keys(filterModelState).length > 0;

      const isResetState = isResetWithDefaults.current;

      if (
        (!dateModified.current || !timeModified.current) &&
        !isFilterApplied &&
        !isResetState
      ) {
        !dateModified.current && setDate(new Date());
        !timeModified.current && setTime(defaultDate?.toString().slice(16, 21));
      } else if (!dateModified.current || !timeModified.current) {
        !dateModified.current && date && setDate(date);
        !timeModified.current && setTime(time);
      }
    }
  };

  return (
    <CsbErrorBoundary>
      <>
        <div
          data-testid="DateTimeInput"
          className={style['custom-date-container']}
        >
          <div role="presentation" ref={refFlatPickr}>
            <input
              className={`ag-date-field-input ${style['custom-date-input']}`}
              type="text"
              ref={refInput}
              data-input
            />
            <div className="input-button" title="clear" data-clear>
              <i className="fa fa-times"></i>
            </div>
          </div>
          <div role="presentation" ref={refFlatTimePickr}>
            <input
              className={`ag-date-field-input ${style['custom-date-input']}`}
              type="text"
              ref={refTimeInput}
              value={time}
              aria-label="Filter Time Value"
              placeholder="Pick time..."
              readOnly
            />
            <div className="input-button" title="clear" data-clear>
              <i className="fa fa-times"></i>
            </div>
          </div>
        </div>
      </>
    </CsbErrorBoundary>
  );
});
